import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';

import type {
  JourneySafetyState,
  PrimaryNavigationItem,
  PrimarySurface,
  ResourceDisplay,
} from '@aztec-realms/shared-types';

import { classNames } from './class-names';
import { GlyphIcon } from './icons';
import { IconButton, QuestCard, ResourceBar } from './primitives';
import { buildSameOriginUrl, sameOriginRoute } from './safe-url';

interface PrimaryNavigationProps {
  items: readonly PrimaryNavigationItem[];
  current: PrimarySurface;
  label?: string;
  disabled?: boolean;
  onNavigate?: (href: string) => void;
}

function handleNavigation(
  event: MouseEvent<HTMLAnchorElement>,
  href: URL | undefined,
  disabled: boolean,
  onNavigate?: (href: string) => void,
) {
  if (disabled || !href) {
    event.preventDefault();
    return;
  }
  if (
    !onNavigate ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  event.preventDefault();
  onNavigate(sameOriginRoute(href));
}

export function PrimaryNavigation({
  items,
  current,
  label = 'Primary navigation',
  disabled = false,
  onNavigate,
}: PrimaryNavigationProps) {
  return (
    <nav aria-label={label} className="ar-primary-nav">
      {items.map((item) => {
        const href = buildSameOriginUrl(item.href);
        const unavailable = disabled || !href;
        return (
          <a
            aria-current={item.id === current ? 'page' : undefined}
            aria-disabled={unavailable || undefined}
            className="ar-primary-nav__item"
            href={href?.href}
            key={item.id}
            onClick={(event) => handleNavigation(event, href, unavailable, onNavigate)}
            tabIndex={unavailable ? -1 : undefined}
          >
            <GlyphIcon className="ar-primary-nav__icon" surface={item.id} />
            <span>{item.label}</span>
          </a>
        );
      })}
    </nav>
  );
}

interface PlayerIdentityChipProps {
  name: string;
  level: number;
  avatarUrl?: string;
  avatarLabel?: string;
  loading?: boolean;
  onActivate?: () => void;
}

export function PlayerIdentityChip({
  name,
  level,
  avatarUrl = '/assets/portraits/wayfinder-citlali-v1.png',
  avatarLabel = 'Dawn Wayfinder',
  loading = false,
  onActivate,
}: PlayerIdentityChipProps) {
  const content = (
    <>
      <span aria-hidden="true" className="ar-player-chip__crest">
        {!loading && (
          <img
            alt=""
            onError={(event) => {
              // Never expose the browser's broken-image chrome as player identity.
              // The material crest remains visible while the PWA retries the asset.
              event.currentTarget.hidden = true;
              event.currentTarget.parentElement?.setAttribute('data-avatar-unavailable', 'true');
            }}
            src={avatarUrl}
          />
        )}
        <span>{loading ? '…' : level}</span>
      </span>
      <span className="ar-player-chip__copy">
        <strong>{loading ? 'Restoring' : name}</strong>
        <span>{loading ? 'Loading profile' : `Level ${level} · ${avatarLabel}`}</span>
      </span>
    </>
  );
  const accessibleLabel = loading
    ? 'Player profile loading'
    : onActivate
      ? `Open profile for ${name}, level ${level}`
      : `${name}, level ${level}`;

  return onActivate ? (
    <button
      aria-busy={loading || undefined}
      aria-label={accessibleLabel}
      className="ar-player-chip"
      data-loading={loading || undefined}
      disabled={loading}
      onClick={onActivate}
      type="button"
    >
      {content}
    </button>
  ) : (
    <div
      aria-busy={loading || undefined}
      aria-label={accessibleLabel}
      className="ar-player-chip"
      data-loading={loading || undefined}
    >
      {content}
    </div>
  );
}

interface ConnectionStatusProps {
  offline: boolean;
}

export function ConnectionStatus({ offline }: ConnectionStatusProps) {
  return (
    <span className="ar-connection" data-offline={offline || undefined} role="status">
      <span aria-hidden="true" className="ar-connection__dot" />
      {offline ? 'Offline · read only' : 'Realm connected'}
    </span>
  );
}

interface TopHUDProps extends PlayerIdentityChipProps {
  inboxAction?: ReactNode;
  resources: readonly ResourceDisplay[];
  offline: boolean;
  interactionLocked?: boolean;
  onNavigate?: (href: string) => void;
}

export function TopHUD({
  inboxAction,
  name,
  level,
  avatarUrl,
  avatarLabel,
  resources,
  loading = false,
  offline,
  interactionLocked = false,
  onNavigate,
}: TopHUDProps) {
  return (
    <header className="ar-top-hud" inert={interactionLocked ? true : undefined}>
      <PlayerIdentityChip
        avatarLabel={avatarLabel}
        avatarUrl={avatarUrl}
        level={level}
        loading={loading}
        name={name}
        onActivate={() =>
          onNavigate ? onNavigate('/profile') : window.location.assign('/profile')
        }
      />
      <ResourceBar compact loading={loading} resources={resources} />
      <div className="ar-top-hud__actions">
        <ConnectionStatus offline={offline} />
        {inboxAction}
        <IconButton
          className="ar-profile-button"
          label="Open profile and settings"
          onClick={() => (onNavigate ? onNavigate('/profile') : window.location.assign('/profile'))}
        >
          <span aria-hidden="true">⚙</span>
          <span className="ar-profile-button__label">Profile</span>
        </IconButton>
      </div>
    </header>
  );
}

interface GameShellProps {
  inboxAction?: ReactNode;
  children: ReactNode;
  currentSurface: PrimarySurface;
  navItems: readonly PrimaryNavigationItem[];
  resources: readonly ResourceDisplay[];
  playerName: string;
  playerLevel: number;
  playerAvatarUrl?: string;
  playerAvatarLabel?: string;
  loading?: boolean;
  contextPanel?: ReactNode;
  compactQuest?: boolean;
  quest?: {
    title: string;
    progressLabel?: string;
    href?: string;
  };
  offline?: boolean;
  journeySafety?: JourneySafetyState;
  className?: string;
  onNavigate?: (href: string) => void;
  onCancelJourney?: () => void;
  onStopJourney?: () => void;
}

export function GameShell({
  inboxAction,
  children,
  currentSurface,
  navItems,
  resources,
  playerName,
  playerLevel,
  playerAvatarUrl,
  playerAvatarLabel,
  loading = false,
  contextPanel,
  compactQuest = false,
  quest,
  offline = false,
  journeySafety = { mode: 'stationary' },
  className,
  onNavigate,
  onCancelJourney,
  onStopJourney,
}: GameShellProps) {
  const journeyPassive = journeySafety.mode === 'journey-passive';
  const shellRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const navigation = navigationRef.current;
    const shell = shellRef.current;
    if (!navigation || !shell) return;
    const hud = shell.querySelector<HTMLElement>('.ar-top-hud');
    const measure = () => {
      const height = navigation.getBoundingClientRect().height;
      if (height > 0) shell.style.setProperty('--ar-mobile-nav-height', `${height}px`);
      const hudHeight = hud?.getBoundingClientRect().height ?? 0;
      if (hudHeight > 0) shell.style.setProperty('--ar-top-hud-height', `${hudHeight}px`);
    };
    measure();
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(navigation);
    if (hud) observer?.observe(hud);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <div
      className={classNames('ar-game-shell', className)}
      data-context={Boolean(contextPanel) || undefined}
      data-journey-passive={journeyPassive || undefined}
      ref={shellRef}
    >
      <a className="ar-skip-link" href="#main-content">
        Skip to realm
      </a>
      <TopHUD
        inboxAction={inboxAction}
        avatarLabel={playerAvatarLabel}
        avatarUrl={playerAvatarUrl}
        interactionLocked={journeyPassive}
        level={playerLevel}
        loading={loading}
        name={playerName}
        offline={offline}
        resources={resources}
        onNavigate={onNavigate}
      />

      <aside className="ar-desktop-navigation" inert={journeyPassive ? true : undefined}>
        <a
          aria-label="Aztec Realms home"
          className="ar-wordmark"
          href="/"
          onClick={(event) =>
            handleNavigation(event, buildSameOriginUrl('/'), journeyPassive, onNavigate)
          }
        >
          <GlyphIcon className="ar-wordmark__icon" surface="home" />
        </a>
        <PrimaryNavigation
          current={currentSurface}
          disabled={journeyPassive}
          items={navItems}
          onNavigate={onNavigate}
        />
      </aside>

      <main className="ar-scene-area" id="main-content" tabIndex={0}>
        {journeyPassive && (
          <div className="ar-journey-banner" role="status">
            <strong>Journey Mode active</strong>
            <span>
              {journeySafety.message ??
                'Discoveries are being queued. Interaction returns when stationary.'}
            </span>
            {(onStopJourney || onCancelJourney) && (
              <div className="ar-journey-banner__actions">
                {onStopJourney && (
                  <button className="ar-journey-banner__stop" onClick={onStopJourney} type="button">
                    Stop when safely stationary
                  </button>
                )}
                {onCancelJourney && (
                  <button
                    className="ar-journey-banner__cancel"
                    onClick={onCancelJourney}
                    type="button"
                  >
                    Cancel · no reward
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        <div className="ar-scene-content" inert={journeyPassive ? true : undefined}>
          {quest && (
            <div
              className={classNames('ar-shell-quest', compactQuest && 'ar-shell-quest--compact')}
            >
              <QuestCard {...quest} onNavigate={onNavigate} />
            </div>
          )}
          {children}
        </div>
      </main>

      {contextPanel && (
        <aside className="ar-context-area" inert={journeyPassive ? true : undefined}>
          {contextPanel}
        </aside>
      )}

      <div
        className="ar-mobile-navigation"
        inert={journeyPassive ? true : undefined}
        ref={navigationRef}
      >
        <PrimaryNavigation
          current={currentSurface}
          disabled={journeyPassive}
          items={navItems}
          onNavigate={onNavigate}
        />
      </div>
    </div>
  );
}
